import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AdminUserItemResponse,
  UserAdminService,
} from '@nebalkon-workspace/admin-api';
import {
  itemMetaLabel,
  itemQuantityBadge,
  itemStatusLabelKey,
} from '@nebalkon-workspace/cabinet-client';
import { OrderApiService } from '@nebalkon-workspace/order-api';
import { PhotoUploadService } from '@nebalkon-workspace/media-api';
import { StorageStatus } from '@nebalkon-workspace/storage-api';
import { NotModal, NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import { CabinetButtonComponent } from '@nebalkon-workspace/cabinet-client';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { filterCatalogPickerItems } from './staff-order-composition.util';
import type {
  StaffOrderCatalogPickerDialogClose,
  StaffOrderCatalogPickerDialogData,
} from './staff-order-catalog-picker-dialog.types';

@Component({
  selector: 'lib-staff-order-catalog-picker-dialog',
  imports: [NotModal, CabinetButtonComponent, TranslatePipe],
  templateUrl: './staff-order-catalog-picker-dialog.component.html',
  styleUrl: './staff-order-catalog-picker-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaffOrderCatalogPickerDialogComponent implements OnInit {
  readonly data = inject(DIALOG_DATA) as StaffOrderCatalogPickerDialogData;
  readonly #dialogRef =
    inject<DialogRef<StaffOrderCatalogPickerDialogClose>>(DialogRef);
  readonly #users = inject(UserAdminService);
  readonly #orders = inject(OrderApiService);
  readonly #photos = inject(PhotoUploadService);
  readonly #translate = inject(TranslateService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly items = signal<AdminUserItemResponse[]>([]);
  protected readonly previewUrls = signal<Record<string, string>>({});
  protected readonly selectedIds = signal<string[]>([]);

  protected readonly canSubmit = computed(
    () => this.selectedIds().length > 0 && !this.saving(),
  );

  protected readonly itemMetaLabel = itemMetaLabel;
  protected readonly itemQuantityBadge = itemQuantityBadge;
  protected readonly itemStatusLabelKey = itemStatusLabelKey;

  ngOnInit(): void {
    this.#loadItems();
  }

  protected previewUrl(itemId: string): string | undefined {
    return this.previewUrls()[itemId];
  }

  protected isSelected(itemId: string): boolean {
    return this.selectedIds().includes(itemId);
  }

  protected toggleSelection(itemId: string): void {
    const current = this.selectedIds();
    if (current.includes(itemId)) {
      this.selectedIds.set(current.filter((id) => id !== itemId));
      return;
    }
    this.selectedIds.set([...current, itemId]);
  }

  protected statusModifier(status: string): string {
    switch (status) {
      case StorageStatus.AtClient:
        return 'at-client';
      case StorageStatus.Pending:
        return 'pending';
      case StorageStatus.InTransitToWarehouse:
        return 'in-transit-warehouse';
      case StorageStatus.Stored:
        return 'stored';
      case StorageStatus.InTransitToClient:
        return 'in-transit-client';
      case StorageStatus.Delivered:
        return 'delivered';
      default:
        return 'unknown';
    }
  }

  protected close(): void {
    this.#dialogRef.close({ added: false });
  }

  protected submit(): void {
    const ids = this.selectedIds();
    if (ids.length === 0 || this.saving()) {
      return;
    }

    this.saving.set(true);
    this.#orders
      .addOrderItems(this.data.orderId, { item_ids: ids })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'success',
            text: [
              this.#translate.instant(
                'admin.storageRequests.orderItemsAddSuccess',
                { count: ids.length },
              ),
            ],
          });
          this.#dialogRef.close({ added: true, count: ids.length });
        },
        error: (err: HttpErrorResponse) => {
          this.saving.set(false);
          const msg =
            err.status === 403
              ? this.#translate.instant('admin.storageRequests.accessDenied')
              : this.#translate.instant(
                  'admin.storageRequests.orderItemsAddError',
                );
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'error',
            text: [msg],
          });
        },
      });
  }

  #loadItems(): void {
    this.loading.set(true);
    this.#users
      .getUserItems(this.data.userId)
      .pipe(
        switchMap((res) => {
          const eligible = filterCatalogPickerItems(
            res.data,
            this.data.storage,
          ).sort((left, right) => left.name.localeCompare(right.name, 'ru'));

          const withPhotos = eligible.filter(
            (item) => (item.photos?.length ?? 0) > 0,
          );
          if (withPhotos.length === 0) {
            return of({ items: eligible, previews: {} as Record<string, string> });
          }

          return forkJoin(
            withPhotos.map((item) =>
              this.#photos.listItemPhotos(item.id).pipe(
                map((photos) => {
                  const main =
                    photos.data.find((photo) => photo.is_main) ??
                    photos.data[0];
                  return main ? ([item.id, main.url] as const) : null;
                }),
                catchError(() => of(null)),
              ),
            ),
          ).pipe(
            map((entries) => {
              const previews: Record<string, string> = {};
              for (const entry of entries) {
                if (entry) {
                  previews[entry[0]] = entry[1];
                }
              }
              return { items: eligible, previews };
            }),
          );
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe({
        next: ({ items, previews }) => {
          this.items.set(items);
          this.previewUrls.set(previews);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'error',
            text: [
              this.#translate.instant(
                'admin.storageRequests.orderItemsCatalogLoadError',
              ),
            ],
          });
        },
      });
  }
}
